/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W2 — GAS HEATER rated-output sheet (§33.5).
 *
 * React port of gas_heater.rated.outputs.tmpl (477 lines). UNLIKE the flat
 * sortable result tables (fan/coil rated rows), the gas_heater output sheet is
 * a TWO-COLUMN label/value grid that branches on the heater type:
 *   - model.type.value == 'id_tubular' (type 1)  → the "tubular" layout
 *   - else (direct_fire / type 3)               → the "direct fire" layout
 * with a per-result pager strip (`.gas-heater-counts-numbers`) rendered for the
 * tubular type (render_heaters, js:2149-2183 — see the pager notes below).
 *
 * It REUSES the shared ratingKit for the cross-cutting pieces the §33 boilerplate
 * calls out: <RatedOn> (the "Rated on" timestamp line — legacy generetionTimeStamp
 * [sic]) and <RatedMessageBlock> (errors/warnings). The bespoke label/value grid
 * is gas_heater-specific (no other selector shares this exact shape), so it lives
 * here rather than in the kit.
 *
 * ── DISPLAY FORMATTING IS NOT UNIFORM, AND THAT IS DELIBERATE ──────────────
 * legacy render_outputs (js:2270-2277) only formats the spans that carry
 * `data-formatter_type`:
 *
 *   for (let input of self.$el[0].querySelectorAll(
 *          'input[type="text"], span[data-formatter_type], select[data-formatter_type]'))
 *     if (c == "integer" || c == "decimal" || c == "float") fn.uni_tools.bindFormatter(input);
 *
 * Every other `.output-label` prints the model value RAW, exactly as the API
 * serialised it. The port formatted almost all of them as decimals, which is
 * visibly wrong wherever the value is not already a 2-dp string — e.g.
 * `face_velocity_actual` is `int(round(...))` server-side
 * (models_component_gas_heater.py:548-555), so dev shows "471" where the port
 * showed "471.00". So: `dec()`/`int()` ONLY on the rows whose template span
 * carries data-formatter_type, `raw()` everywhere else, and `getOneDecimal()`
 * for the `{{get_one_decimal}}` helper (Number(x).toFixed(2), no separators,
 * 0 → '' — handlebars/helpers.js:154-157).
 */

import { useState } from 'react';
import { RatedOn, RatedMessageBlock } from './ratingKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';

export interface GasHeaterRatedModel {
  /** Sync K (gas_heater_serializers.py): master_gas_heater.* passthroughs
   * powering the new tubular output rows. All read-only, nullable. */
  min_airflow?: number | null;
  max_airflow?: number | null;
  firing_mode_name?: string | null;
  turn_down_name?: string | null;
  efficiency?: number | null;
  [key: string]: unknown;
}

interface OutRow {
  label: string;
  value: React.ReactNode;
  unit?: string;
  /** `percent right` | `percent right temp` (temp draws the degree mark) |
   * `percent` (the tubular Burner Manifold Pressure row, tmpl:120-124). */
  unitClass?: string;
  /** legacy `{{#if …}}` / `class="hide{{…}}"` rows: the SOURCE value the
   * template tested, not the formatted string. undefined ⇒ always render. */
  showIf?: unknown;
  /** the tubular Burner Manifold Pressure label-part carries inline flex
   * (tmpl:121). */
  labelStyle?: React.CSSProperties;
  /** label column width: 5 in the left output column, 6 in the right one. */
  labelCols?: 5 | 6;
  /** the div wrapping the value span — col-xs-12 except on two tubular rows. */
  valueCols?: 6 | 12;
}

/** data-formatter_type="decimal" (thousands separators + 2 dp). */
const dec = (v: unknown) => (v == null || v === '' ? '' : formatDecimalDisplay(v));
/** data-formatter_type="integer" (thousands separators, no decimals). */
const int = (v: unknown) => (v == null || v === '' ? '' : formatIntegerDisplay(v));
/** NO data-formatter_type — handlebars prints the value as-is (null/undefined
 * render as the empty string, NOT "null"). */
const raw = (v: unknown) => (v == null ? '' : String(v));
/** `get_one` helper: first truthy non-object of the args, else '' (handlebars/
 * helpers.js:149-151). */
const one = (...vals: unknown[]): unknown =>
  vals.find((v) => v && typeof v !== 'object') ?? '';
/** `get_one_decimal` helper: Number(get_one).toFixed(2), '' when falsy —
 * note NO thousands separator and 0 → '' (handlebars/helpers.js:154-157). */
const getOneDecimal = (...vals: unknown[]): string => {
  const res = Number(one(...vals));
  return res ? res.toFixed(2) : '';
};
/**
 * Handlebars `{{#if}}` truthiness (handlebars' isEmpty): undefined, null, '',
 * false, 0 and [] are empty. NB `0` is falsy but the string "0.00" is not —
 * DRF serialises DecimalFields as strings, so a zero decimal still renders,
 * exactly as on dev.
 */
const hbTruthy = (v: unknown): boolean =>
  !(v == null || v === '' || v === false || v === 0 || (Array.isArray(v) && !v.length));

/**
 * A single label : value row (legacy `.label-part` | `.output-label`).
 *
 * The port had the split INVERTED — `col-xs-7` label / `col-xs-5` value where
 * legacy is 5/7 in the left output column and 6/6 in the right one — so output
 * labels ran wider than their input-side counterparts and the values sat in a
 * narrow column that did not line up with the grid above. It also dropped the
 * `.row > .col-xs-12` wrapper legacy puts around the value span.
 */
const Row = ({
  label,
  value,
  unit,
  unitClass = 'percent right',
  showIf,
  labelStyle,
  labelCols = 5,
  valueCols = 12,
}: OutRow) => {
  if (showIf !== undefined && !hbTruthy(showIf)) return null;
  return (
    // legacy's row div carries no extra class (`<div class="row">`) — the port
    // added `output-row`, which nothing styles but which is not what dev emits.
    <div className="row">
      <div className={`col-xs-${labelCols} label-part`} style={labelStyle}>
        <span>{label}</span>
        {unit && <span className={unitClass}>{unit}</span>}
      </div>
      <div className={`col-xs-${12 - labelCols} input-part`}>
        <div className="row">
          {/* two tubular rows put the value in a col-xs-6 (tmpl:106/117). */}
          <div className={`col-xs-${valueCols}`}>
            <span className="output-label">{value}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/** legacy `self.items_count = 9` (js:238) — the pager window size. */
const GH_PAGER_ITEMS = 9;

/**
 * The result pager (legacy render_heaters → items_list.tmpl, js:2149-2183 +
 * showOrHideButtons/prev_next :2185-2214).
 *
 * The port rendered bare numbered spans and only when there was MORE THAN ONE
 * result, so the strip never appeared at all: the selector hard-coded count 1.
 * legacy renders it for every tubular result set (`render_heaters(count === 1 ?
 * [model] : gas_heaters_list)`) — a single result still draws its "1" chip —
 * and wraps the chips in the prev/next arrows, which stay `hide` until the list
 * is longer than the 9-item window and then scroll it one step at a time.
 */
const GasHeaterPager = ({
  count,
  activeIndex,
  onSelectResult,
}: {
  count: number;
  activeIndex: number;
  onSelectResult?: (index: number) => void;
}) => {
  const [firstVisible, setFirstVisible] = useState(0);
  const lastVisible = firstVisible + GH_PAGER_ITEMS;
  const paged = count > GH_PAGER_ITEMS;
  const prevDisabled = firstVisible === 0;
  const nextDisabled = lastVisible >= count - 1;
  const arrow = (dir: -1 | 1) => {
    const disabled = dir < 0 ? prevDisabled : nextDisabled;
    return (
      <button
        id={dir < 0 ? 'prev_item' : 'next_item'}
        type="button"
        data-step={dir}
        className={`${dir < 0 ? 'icon-angle-left' : 'icon-angle-right'} prev_next_item${
          !paged ? ' hide' : ''
        }${disabled ? ' non-clickable' : ''}`}
        onClick={disabled || !paged ? undefined : () => setFirstVisible((f) => f + dir)}
      />
    );
  };
  return (
    <div
      className={`gas_heater-counts gas_heater_items_count col-xs-12 ${
        count > 10 ? 'justify-space-between' : 'gap-5'
      }`}
    >
      {arrow(-1)}
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          data-id={i}
          className={`item item_${i} gas-heater-counts-numbers gas_heater_counts_numbers${
            i === activeIndex ? ' active' : ''
          }${paged && (i < firstVisible || i > lastVisible) ? ' hide' : ''}`}
          role={onSelectResult ? 'button' : undefined}
          onClick={onSelectResult ? () => onSelectResult(i) : undefined}
        >
          {i + 1}
        </div>
      ))}
      {arrow(1)}
    </div>
  );
};

export const GasHeaterRatedOutputs = ({
  model,
  ratedModel,
  ratedDatetime,
  errors,
  warnings,
  ratedCount,
  activeIndex,
  onSelectResult,
}: {
  /** the saved/rated gas_heater model (the legacy `model.toJSON()`). */
  model: GasHeaterRatedModel | null;
  /** the picked rated row — the MASTER CATALOG row legacy passes as
   * `rated_model` (loadParams' gas_heaters_list entry), not the AHU model. */
  ratedModel?: GasHeaterRatedModel | null;
  ratedDatetime?: string | null;
  errors?: string[];
  warnings?: string[];
  /** number of rated rows (drives the pager). */
  ratedCount?: number;
  /** the active result index (legacy data-id, 0-based). */
  activeIndex?: number;
  /** result pager click (legacy changeGasHeater → re-search at index). */
  onSelectResult?: (index: number) => void;
}) => {
  if (!model) return null;
  const r = (ratedModel ?? {}) as GasHeaterRatedModel;
  const typeValue = ((model.type as { value?: string } | undefined)?.value ?? '').toString();
  const isTubular = typeValue === 'id_tubular';
  // tmpl:22 gates the pane's inline padding/height on RATED_MODEL.type.value —
  // a catalog row has only `type_id`, so this fires solely on the merged
  // fallback rated_model of a direct-fire heater.
  const directFirePane = (r.type as { value?: string } | undefined)?.value === 'direct_fire';

  // burner manifold actual falls back to conditional — legacy patches the
  // MODEL before rendering (render_outputs js:2248), so the {{#if}} gate sees
  // the fallback too.
  const burnerManifoldActual =
    model.burner_manifold_pressure_actual || model.burner_manifold_pressure_conditional;

  const tubularLeft: OutRow[] = [
    // tmpl:36-45 — `{{rated_model.model}}`: the CATALOG row's model, with no
    // fallback to the AHU model's own field.
    { label: 'Model', value: raw(r.model) },
    { label: 'Number of Heat-Exchangers', value: raw(r.heatex_qty) },
    {
      label: 'Gas Inlet NPT Size',
      unit: 'in',
      value: getOneDecimal(model.gas_in_conn_dia_od, r.gas_in_conn_dia_od),
    },
    {
      label: 'Gas Inlet Hand',
      value: raw(one(r.conn_hand_name, model.conn_hand_name)),
      valueCols: 6,
    },
    // tmpl:112-116 — the unit span on this row really is "in" on dev.
    {
      label: 'Qty Wide x High',
      unit: 'in',
      value: `${raw(model.qty_wide)} x ${raw(model.qty_high)}`,
      valueCols: 6,
    },
    {
      label: 'Burner Manifold Pressure',
      unit: 'in WC',
      unitClass: 'percent',
      labelStyle: { display: 'flex', justifyContent: 'space-between', whiteSpace: 'nowrap' },
      value: dec(burnerManifoldActual),
      showIf: burnerManifoldActual,
    },
    {
      label: 'Pilot Manifold Pressure',
      unit: 'in WC',
      value: raw(model.pilot_manifold_gas_pressure),
      showIf: model.pilot_manifold_gas_pressure,
    },
    // tmpl:155-166 — `class="hide{{get_one …}}"`: the row is hidden by the
    // bare `hide` class when get_one yields '' and visible otherwise. The port
    // dropped the row entirely.
    {
      label: 'Turn down',
      value: raw(one(model.turn_down_actual, model.turndown, model.turndown_name, r.turndown_name)),
      showIf: one(model.turn_down_actual, model.turndown, model.turndown_name, r.turndown_name),
    },
    { label: 'Voltage', value: raw(model.voltage), showIf: model.voltage },
    {
      label: 'Full Load Current total',
      unit: 'Amp',
      value: dec(model.gas_heater_fla),
      showIf: model.gas_heater_fla,
    },
  ];
  // Sync K (tmpl @dev): "Firing Rate" is REPLACED by the master-row airflow
  // envelope (Airflow Minimum/Maximum, integer-formatted), and Firing Mode /
  // Turn Down / Efficiency follow Leaving Air Temperature. Face Velocity unit
  // is uppercase "FPM" here (the direct-fire block keeps lowercase "fpm").
  const tubularRight: OutRow[] = [
    {
      label: 'Capacity',
      unit: 'btu/hr',
      value: `${int(model.output_rating_actual)} / ${int(model.output_rating_actual_total)}`,
    },
    { label: 'Airflow Minimum', unit: 'CFM', value: int(model.min_airflow), showIf: model.min_airflow },
    { label: 'Airflow Maximum', unit: 'CFM', value: int(model.max_airflow), showIf: model.max_airflow },
    {
      label: 'Leaving Air Temperature',
      unit: 'F',
      unitClass: 'percent right temp',
      value: raw(model.ladb_temp_actual),
      showIf: model.ladb_temp_actual,
    },
    { label: 'Firing Mode', value: raw(model.firing_mode_name), showIf: model.firing_mode_name },
    { label: 'Turn Down', value: raw(model.turn_down_name), showIf: model.turn_down_name },
    { label: 'Efficiency', unit: '%', value: raw(model.efficiency), showIf: model.efficiency },
    { label: 'Face Velocity', unit: 'FPM', value: raw(model.face_velocity_actual) },
    { label: 'Air Pressure Drop', unit: 'in WC', value: raw(model.apd) },
  ];

  // Sync K (tmpl @dev): Burner Manifold Pressure / Pilot Manifold Pressure /
  // Turn down moved LEFT column → RIGHT column (between Leaving Air
  // Temperature and Face Velocity, in that order); Burner Manifold Pressure
  // gained data-formatter_type="decimal" (dec() here).
  const directLeft: OutRow[] = [
    { label: 'Model', value: raw(model.model) },
    { label: 'Burner total length', value: raw(model.burner_length_linear_total) },
    { label: 'Burner Area total', unit: 'ft2', value: raw(model.burner_area_total) },
    {
      label: 'Gas Inlet NPT Size',
      unit: 'in',
      value: getOneDecimal(model.gas_in_pipe_npt_size, model.gas_in_conn_dia_od, r.gas_in_conn_dia_od),
    },
    { label: 'Voltage', value: raw(model.voltage), showIf: model.voltage },
    {
      label: 'Full Load Amp',
      unit: 'Amp',
      value: dec(model.gas_heater_fla),
      showIf: model.gas_heater_fla,
    },
  ];
  const directRight: OutRow[] = [
    { label: 'Capacity Required total', unit: 'btu/hr', value: int(model.output_rating_required_total) },
    { label: 'Capacity total', unit: 'btu/hr', value: int(model.output_rating_conditional_total) },
    {
      label: 'Firing Rate',
      unit: 'btu/hr/ft',
      value: int(model.firing_rate_actual),
      showIf: model.firing_rate_actual,
    },
    {
      label: 'Leaving Air Temperature',
      unit: 'F',
      unitClass: 'percent right temp',
      value: raw(one(model.ladb_temp_actual, model.ladb_temp_desired)),
    },
    { label: 'Burner Manifold Pressure', unit: 'in WC', value: dec(model.burner_manifold_pressure_actual) },
    { label: 'Pilot Manifold Pressure', unit: 'in WC', value: raw(model.pilot_manifold_gas_pressure) },
    { label: 'Turn down', value: raw(model.turn_down_actual) },
    { label: 'Face Velocity', unit: 'fpm', value: raw(model.face_velocity_actual) },
    { label: 'Air Pressure Drop', unit: 'in WC', value: raw(model.apd) },
  ];

  const left = isTubular ? tubularLeft : directLeft;
  const right = isTubular ? tubularRight : directRight;

  return (
    <>
      {/* The OUTPUT tab nav (tmpl:1-15). The port dropped it entirely, so the
          bordered output box ran the full width where dev shows an "Output /
          Heater / Performance" rail in a col-xs-1 gutter and the box in the
          remaining col-xs-11 — the same left rail the input half has. Both
          entries point at the one pane, exactly as in legacy.

          The `href` is load-bearing for keyboard use, not decoration: an <a>
          without one is not focusable, so dev3 skipped both entries when you
          tabbed off the last input while dev stops on each. Legacy's are
          `<a data-toggle="tab" href="#…Properties-Performance_output">`, and
          bootstrap's tab plugin preventDefaults the click — mirrored here so
          the hash is never actually written. */}
      <div className="col-xs-1 nav nav-tabs">
        <ul className="row">
          <li className="cursor-default">Output</li>
          <li className="ahu-Properties-output output_label selected">
            <a
              className="output_tab"
              data-toggle="tab"
              href="#ahu-Properties-Performance_output"
              onClick={(e) => e.preventDefault()}
            >
              Heater
            </a>
          </li>
          <li className="ahu-Performance-output output_label selected">
            <a
              className="output_tab"
              data-toggle="tab"
              href="#ahu-Properties-Performance_output"
              onClick={(e) => e.preventDefault()}
            >
              Performance
            </a>
          </li>
        </ul>
      </div>

      <div className="col-xs-11 output-with-border">
        <RatedMessageBlock errors={errors} warnings={warnings} />

        {/* result pager (tmpl:16-20) — the strip is tubular-only, and legacy
            emits its (initially empty) container for every tubular render. */}
        {isTubular && (
          <GasHeaterPager
            count={ratedCount ?? 0}
            activeIndex={activeIndex ?? 0}
            onSelectResult={onSelectResult}
          />
        )}

        {/* tmpl:22-25 — the pane keeps the legacy id/name, and direct fire
            carries the inline padding/height override. The grid row below is
            `class="{{rated_model.type_id}} row row no-right-padding"`; the
            port swapped the (duplicated) `row` + type-id classes for
            `output-data`, a class the rating stylesheet gives meaning to. */}
        <div
          className="col-xs-12 tab-pane fade active in full-height"
          id="ahu-Properties-Performance_output"
          {...{ name: 'ahu-Properties_Performance_Output' }}
          style={directFirePane ? { paddingTop: 12 } : undefined}
          // the height half of that inline override has to beat
          // `.full-height {height:100%!important}`, so it can only be set
          // imperatively — React's style prop cannot emit !important.
          ref={(el) => {
            if (!el) return;
            if (directFirePane) el.style.setProperty('height', 'calc(100% - 45px)', 'important');
            else el.style.removeProperty('height');
          }}
        >
          <div className={`${raw(r.type_id)} row row no-right-padding`}>
            <div className="col-xs-12 col-md-12 col-lg-6">
              {left.map((row, i) => (
                <Row key={i} {...row} />
              ))}
            </div>
            <div className="col-xs-12 col-md-12 col-lg-6">
              {right.map((row, i) => (
                <Row key={i} {...row} labelCols={6} />
              ))}
            </div>
          </div>
        </div>

        <RatedOn datetime={ratedDatetime} />
      </div>
    </>
  );
};

export default GasHeaterRatedOutputs;
